<template>
  <v-dialog v-model="showTaggerModal">

    <v-card>

      <v-card-title>
        <v-row>
          <v-col>
            <div class="float-left">Edit Tags</div>
          </v-col>
          <v-col>
            <div class="float-right">

              <v-btn  class='ma-1' @click="closeTaggerModal()">Cancel</v-btn>
              <v-btn :disabled="!hasChanges" class='ma-1' color="primary" @click="saveChanges()">Save Changes</v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <inner-image-zoom :src="photo_rec.image_url" className="tag-image-zoom" moveType="drag"/>
          </v-col>

          <v-col cols="6">
            <v-row>
              <v-col cols="12">
                <v-chip v-for="tag in editableTags" :key="tag.id"
                        class="ma-1 v-size--small"
                        close
                        @click:close="removeTag(tag)"
                >
                  {{ tag.name }}
                  <v-chip
                      outlined
                      props: style="border-width: 0;"
                      x-small

                  >
                    <v-spacer>{{ tag.category.name }}</v-spacer>
                  </v-chip>
                </v-chip>


              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-select
                    v-model="selectedCategory"
                    :items="allCats"
                    item-text="name"
                    item-value="id"
                    label="Category"
                    @change="filterTags(selectedCategory)"
                ></v-select>
              </v-col>
              <v-col cols="6">
                <v-select
                    v-model="selectedTags"
                    :items="filteredTags"
                    chips
                    :item-text="tagLabel"
                    item-value="id"
                    label="Tags"
                    multiple
                    return-object
                ></v-select>
              </v-col>
              <v-col cols="2">
                <v-btn :disabled="!selectedTags.length" color="primary" @click="addTags()">Add Tags</v-btn>
              </v-col>
            </v-row>
          </v-col>

        </v-row>
      </v-card-text>

    </v-card>

  </v-dialog>
</template>

<script>
import 'vue-inner-image-zoom/lib/vue-inner-image-zoom.css'
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex'
import InnerImageZoom from 'vue-inner-image-zoom'

export default {
  name: 'TaggerModal',
  components: {
    'inner-image-zoom': InnerImageZoom
  },
  props: {
    photo_rec: Object
  },
  data() {
    return {
      searchInput: null,
      selectedCategory: null,
      selectedTags: [],
      filteredTags: [],
      isModalVisible: false,
      hasChanges: false,
      // IT-703: the PhotoGroup that owns this photo, on group-mode tenants.
      // Null means "not loaded" and saveChanges refuses to write, because
      // writing without it would replace the group's tags with an empty set.
      groupForPhoto: null,
      // IT-703: the tags being edited, held locally. Writing them onto
      // `photo_rec` mutated the parent's record — PhotoCardV2 renders it as
      // closable chips whose X posts to the PHOTO endpoint, so simply opening
      // this dialog armed a two-click path that put group tags on the photo.
      editableTags: [],
      // mounted() starts the group lookup un-awaited; saving during that
      // window either did nothing or was overwritten by the late result.
      isLoadingGroup: false,
    }
  },
  mounted() {
    // this.setFilteredTags(this.$store.state.tags.tags)
    // this.setSelectedTagsModal(this.$store.state.tags.selectedTags)
    this.filteredTags = this.visibleTags
    this.hasChanges = false
    // IT-703: PhotoCardV2 renders this dialog behind `v-if`, so it mounts
    // fresh on every open — this is the entry point, not the similarly named
    // showTaggerModal() method, which nothing calls. Loading here is what
    // lets group mode edit the group's tags instead of the photo's.
    this.loadGroupForPhoto()

  },
  computed: {
    ...mapGetters(['getCategoryNames', 'getTagNames', 'isGroupMode', 'visibleCategories', 'visibleTags', 'retainHumanTags']),
    ...mapState({

      tags: state => state.tags.tags,
      categories: state => state.categories.categories,

      allCats() {
        // IT-704 (P4): hide machine-owned (derived) categories once enabled.
        var cats = this.visibleCategories
        cats = cats.map(cat => ({id: cat.id, name: cat.name}))
        cats.unshift({id: 0, name: 'All'})

        return cats
      }
    })
  },
  methods: {
    ...mapMutations(['setMessage']),
    ...mapActions([
      'bulkUpdate',
      'bulkUpdateGroup',
      'fetchGroupForPhoto',
      'submitAssigned',
    ]),
    filterTags(e) {
      if (e !== 0) {
        var filteredTags = this.visibleTags.filter(tag => tag.category.id === parseInt(e))
      } else {
        filteredTags = this.visibleTags
      }
      this.filteredTags = filteredTags
    },
    selectedTagsChanged(tags) {
      for (let tag of tags) {
        if (!this.photoHasTag(tag)) {
          this.editableTags.push(tag)
        }
      }
    },
    addTags() {
      if (this.isLoadingGroup) return
      for (let tag of this.selectedTags) {
        if (!this.photoHasTag(tag)) {
          this.editableTags.push(tag)
          this.hasChanges = true
        }
      }
      this.selectedTags = []
    },
    photoHasTag(tag) {
      for (let photoTag of this.editableTags) {
        if (photoTag.id === tag.id) {
          return true
        }
      }
      return false
    },
    tagLabel(tag) {
      // IT-703: Family Dollar's catalog has tags named 1-5 in two categories
      // (OOS Score, Cleanliness/Condition Score), so the bare name renders as
      // "1, 1, 2, 2, 3, 3" and is ambiguous by construction. GroupTagging
      // already qualifies its own selector this way. The category is optional
      // in the catalog, so fall back to the bare name rather than "(undefined)".
      const category = tag && tag.category && tag.category.name
      return category ? `${tag.name} (${category})` : `${tag.name}`
    },

    async loadGroupForPhoto() {
      // IT-703: on a group-mode tenant the tags live on the PhotoGroup, and
      // the group endpoint REPLACES the set rather than merging. The editor
      // must therefore start from the group's current tags — seeding from the
      // photo's own (empty in group mode) and posting that would erase the
      // tagger's work on the first save.
      if (!this.isGroupMode) {
        // Photo mode edits the photo's own tags, exactly as before.
        // IT-704 (P4): seed only human-picked tags when derivation is on; the
        // spread still copies (never alias photo_rec.tags — IT-703).
        this.editableTags = [...this.retainHumanTags(this.photo_rec.tags)]
        return
      }
      this.isLoadingGroup = true
      try {
        const group = await this.fetchGroupForPhoto({
          response_group_id: this.photo_rec.response_group_id,
          question_id: this.photo_rec.question_id,
        })
        this.groupForPhoto = group || null
        // Into local state, never onto the prop — see `editableTags` above.
        // IT-704 (P4): seed only human-picked tags when derivation is on.
        this.editableTags = group ? [...this.retainHumanTags(group.tags)] : []
      } catch (e) {
        // Leave groupForPhoto null; saveChanges refuses to write without it.
        this.groupForPhoto = null
      } finally {
        this.isLoadingGroup = false
      }
    },

    async saveChanges() {
      if (this.isLoadingGroup) return
      // IT-704 (P4): send only human-picked tags when derivation is on.
      const currentTags = this.retainHumanTags(this.editableTags).map(tag => tag.id)

      try {
        if (this.isGroupMode) {
          // No group means no safe write. The group endpoint REPLACES the tag
          // set, so writing without having read it would erase the group's
          // tags; falling back to the photo would hide them instead.
          if (!this.groupForPhoto) return
          await this.bulkUpdateGroup({
            group_id: this.groupForPhoto.id,
            selected_tags: currentTags,
            isQc: false,
            // Preserve what QC set. The photo path guards this deliberately
            // ("tagging save shouldn't toggle it"); hardcoding false here let
            // a non-QC save drop the group out of the needs-review queue.
            needs_review: !!this.groupForPhoto.needs_review,
          })
        } else {
          await this.bulkUpdate({
            selected_photos: [this.photo_rec.id],
            selected_tags: currentTags,
            isQc: false,
            needs_review: false,
          })
        }
      } catch (e) {
        // Leave the dialog open with the edit intact — closing on failure is
        // the silent-success bug this codebase has been digging out all week.
        this.setMessage({
          text: 'Could not save these tags. Please try again.',
          type: 'error',
          alert: true,
        })
        return
      }

      this.hasChanges = false
      this.closeTaggerModal()
    },
    showTaggerModal() {
      this.isModalVisible = true
    },
    closeTaggerModal() {
      this.isModalVisible = false
      this.$emit('close')
    },
    removeTag(tag) {
      for (let index = this.editableTags.length - 1; index >= 0; index--) {
        if (this.editableTags[index].id === tag.id) {
          this.editableTags.splice(index, 1)
          this.hasChanges = true
        }
      }
    }
  }
}
</script>

<style>
.v-chip .v-chip__content {
  height: auto !important;
}

.flex-container {
  display: flex;
  justify-content: center;
}

#image-container {
  flex: 1;
  margin: auto;
  padding-left: 1vw;
  padding-top: 1vh;
}

#tagging-menu {
  flex: 2;
  max-width: 100%;
  padding-left: 1vw;
  padding-right: 1vw;
  margin: auto;
}

.tag-image-zoom {
  height: 90%;
  max-height: 90%;
}

@media (min-width: 960px) {
  .tag-image-zoom {
    width: 480px;
    max-width: 480px;
  }
}

@media (min-width: 1264px) {
  .tag-image-zoom {
    width: 632px;
    max-width: 632px;
  }
}

@media (min-width: 1904px) {
  .tag-image-zoom {
    width: 920px;
    max-width: 920px;
  }
}


</style>
